import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../components/Utils/ModalShow';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import PayslipStateBadge from './PayslipStateBadge';
import { FaPrint, FaPaperPlane, FaFileInvoiceDollar } from 'react-icons/fa';

// Utilitaire pour convertir un blob en base64
const pdfBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const PayslipModal = ({ isOpen, onClose, employee, month, year, onUpdate }) => {
    const [payslip, setPayslip] = useState(null);
    const [pdfUrl, setPdfUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    const selectedMonth = `${year}-${String(month).padStart(2, '0')}`;
    const employeeId = employee?.employee_id;

    const fetchPayslipDetails = useCallback(async () => {
        if (!employeeId || !selectedMonth) return;
        setIsLoading(true);
        setPdfUrl('');
        setPayslip(null);
        try {
            const response = await apiService.getElement(`/payslip/employee/${employeeId}/month/${selectedMonth}`);
            if (response.data.found) {
                const fetchedPayslip = response.data.payslip;
                setPayslip(fetchedPayslip);
                const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${fetchedPayslip.id}`);
                const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                setPdfUrl(url);
            }
        } catch (error) {
            console.error("Erreur lors de la récupération du bulletin", error);
            notify("Erreur lors de la récupération du bulletin", null, 3);
        } finally {
            setIsLoading(false);
        }
    }, [employeeId, selectedMonth]);

    useEffect(() => {
        if (isOpen) {
            fetchPayslipDetails();
        }
    }, [isOpen, fetchPayslipDetails]);

    const handlePrint = async () => {
        if (!pdfUrl) return;
        setIsPrinting(true);
        try {
            const link = document.createElement('a');
            link.href = pdfUrl;
            link.setAttribute('download', `bulletin-${payslip.Employee}-${selectedMonth}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            notify("Bulletin téléchargé avec succès.", null, 2);
        } catch (e) {
            notify("Erreur lors du téléchargement du bulletin.", null, 3);
        } finally {
            setIsPrinting(false);
        }
    };

    const monthName = new Date(year, month - 1).toLocaleDateString('fr-FR', { month: 'long' });

    const handleSend = async () => {
        if (!payslip || !payslip.email) {
            notify("L'email de l'employé est manquant.", null, 4);
            return;
        }
        if (!pdfUrl) {
            notify("Le bulletin PDF n'est pas disponible.", null, 4);
            return;
        }
        setIsSending(true);
        try {
            const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${payslip.id}`);
            const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
            const base64data = await pdfBlobToBase64(blob);
            await apiService.sendEmail({
                to: payslip.email,
                subject: `Votre bulletin de paie - ${monthName} ${year}`,
                html: `<p>Bonjour ${payslip.Employee || ''},</p><p>Veuillez trouver votre bulletin de paie pour le mois de ${monthName} ${year} en pièce jointe.</p><p>Cordialement,</p>`,
                template_id: 1,
                params: {
                    prenom: payslip.Employee || "l'employé",
                    month: `${monthName} ${year}`,
                },
                attachment: {
                    filename: `bulletin-${payslip.Employee}-${selectedMonth}.pdf`,
                    content: base64data,
                },
            });

            notify("Bulletin envoyé avec succès.", null, 2);
        } catch (e) {
            console.error("Erreur lors de l'envoi du bulletin:", e);
            notify("Une erreur est survenue lors de l'envoi.", null, 3);
        } finally {
            setIsSending(false);
        }
    };

    const renderFooter = () => (
        <div className="flex items-center justify-between w-full gap-3">
            <div>
                {payslip && <PayslipStateBadge state={payslip.State} />}
            </div>
            <div className="flex items-center gap-3">
                <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium">Fermer</button>
                {payslip && (
                    <>
                        <button
                            onClick={handleSend}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 rounded-lg font-medium"
                            disabled={isLoading || isSending || !payslip.email}
                            title={!payslip.email ? "Email de l'employé non disponible" : "Envoyer par email"}
                        >
                            {isSending ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <FaPaperPlane />
                            )}
                            {isSending ? "Envoi..." : "Envoyer"}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-[--color-primary] text-white rounded-lg font-medium"
                            disabled={!pdfUrl || isLoading || isPrinting}
                        >
                            {isPrinting ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <FaPrint />
                            )}
                            {isPrinting ? "Téléchargement..." : "Imprimer"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Bulletin de ${employee?.employee_name || ''} `}
            subtitle={`${monthName} ${year}`}
            size={900}
            footer={renderFooter()}
        >
            <div className="p-6 min-h-[60vh] max-h-[75vh] flex flex-col">
                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="loading loading-spinner text-primary text-lg"></div>
                    </div>
                ) : pdfUrl ? (
                    <div className="flex-1 overflow-y-auto">
                        <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
                            <Viewer fileUrl={pdfUrl} />
                        </Worker>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <p>Aucun bulletin trouvé pour cet employé pour ce mois.</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PayslipModal;